import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import NewFormFilling, {
  PublicFormExecution,
} from "../components/NewFormFilling/Structure/NewFormFilling";
import CheckinFluxoPublico, {
  AberturaCheckinFluxo,
  AgendamentoPublico,
  LocalizacaoPublica,
  ValidacaoCheckinFluxo,
} from "../service/CheckinFluxoPublico";
import Anonimo from "../service/Anonimo";
import EstrategiaIdAgendamentoService from "../service/EstrategiaIdAgendamento";
import {
  camposDaEstrategia,
  labelDoCampoIdAgendamento,
  montarIdentificadorAgendamento,
  todosCamposIdAgendamentoPreenchidos,
} from "../helpers/estrategiaIdAgendamento";
import { FormToFilling } from "../types/new-form-filling/types";

const checkinService = new CheckinFluxoPublico();
const anonimoService = new Anonimo();
const estrategiaIdAgendamentoService = new EstrategiaIdAgendamentoService();

function mensagemErro(error: unknown) {
  if (!axios.isAxiosError(error)) return "publicCheckin.errors.generic";
  if (error.response?.status === 401) return "publicCheckin.errors.expired";
  if (error.response?.status === 429)
    return "publicCheckin.errors.attemptLimit";
  const dados = error.response?.data as
    | { error?: string; message?: string }
    | undefined;
  if (dados?.error === "Localização fora da cerca permitida.")
    return "publicCheckin.errors.outsideGeofence";
  if (error.response?.status === 422)
    return "publicCheckin.errors.invalidLocation";
  return dados?.message || "publicCheckin.errors.generic";
}

async function obterLocalizacao(): Promise<LocalizacaoPublica | undefined> {
  if (!navigator.geolocation) return undefined;
  return new Promise((resolve) =>
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => resolve(undefined),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    ),
  );
}

function ResumoAgendamento({
  agendamento,
  dataFormatada,
}: {
  agendamento: AgendamentoPublico;
  dataFormatada: string;
}) {
  const resumo = agendamento.resumoAgendamento;
  return (
    <Stack spacing={1} paddingTop={1}>
      {resumo?.titulo && <Typography variant="h6">{resumo.titulo}</Typography>}
      {resumo?.subtitulo && <Typography>{resumo.subtitulo}</Typography>}
      {resumo?.detalhes?.map((detalhe) => (
        <Typography key={detalhe} color="text.secondary">
          {detalhe}
        </Typography>
      ))}
      {agendamento.dataPrevista && (
        <Typography color="text.secondary">{dataFormatada}</Typography>
      )}
    </Stack>
  );
}

export default function Public() {
  const { t, i18n } = useTranslation();
  const { aliasEmpresa, aliasChecklist } = useParams<{
    aliasEmpresa: string;
    aliasChecklist: string;
  }>();
  const [valoresIdentificador, setValoresIdentificador] = useState<
    Record<string, string>
  >({});
  const [validacao, setValidacao] = useState<ValidacaoCheckinFluxo | null>(
    null,
  );
  const [abertura, setAbertura] = useState<AberturaCheckinFluxo | null>(null);
  const [localizacao, setLocalizacao] = useState<
    LocalizacaoPublica | undefined
  >();
  const [avisoLocalizacao, setAvisoLocalizacao] = useState(false);
  // Mantém a mesma execução anônima quando o envio de concluir falhar e o
  // visitante tentar novamente, evitando criar um segundo histórico.
  const historicoAnonimo = useRef<{
    respostas: FormToFilling;
    inicio: number;
  } | null>(null);

  const sessaoQuery = useQuery({
    queryKey: ["CHECKIN_FLUXO_SESSAO", aliasEmpresa, aliasChecklist],
    enabled: Boolean(aliasEmpresa && aliasChecklist),
    queryFn: () =>
      checkinService.iniciar(aliasEmpresa as string, aliasChecklist as string),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const estrategiaQuery = useQuery({
    queryKey: ["ESTRATEGIA_ID_AGENDAMENTO", sessaoQuery.data?.empresa],
    enabled: Boolean(sessaoQuery.data?.empresa),
    queryFn: () =>
      estrategiaIdAgendamentoService.obter(sessaoQuery.data?.empresa as string),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const abrir = useMutation({
    mutationFn: async () => {
      setAvisoLocalizacao(true);
      const posicao = await obterLocalizacao();
      setLocalizacao(posicao);
      return checkinService.abrir(
        aliasEmpresa as string,
        aliasChecklist as string,
        sessaoQuery.data?.sessao as string,
        posicao,
      );
    },
    onSuccess: setAbertura,
  });
  const validar = useMutation({
    mutationFn: () => {
      const estrategia = estrategiaQuery.data;
      if (!estrategia) {
        throw new Error("Estratégia de identificação indisponível.");
      }
      return checkinService.validar(
        aliasEmpresa as string,
        aliasChecklist as string,
        sessaoQuery.data?.sessao as string,
        montarIdentificadorAgendamento(
          estrategia,
          valoresIdentificador,
        ),
      );
    },
    onSuccess: setValidacao,
  });
  const continuar = useMutation({
    mutationFn: () =>
      checkinService.continuarSemAgendamento(
        aliasEmpresa as string,
        aliasChecklist as string,
        sessaoQuery.data?.sessao as string,
      ),
    onSuccess: () => abrir.mutate(),
  });
  const publicExecution = useMemo<PublicFormExecution | undefined>(
    () =>
      abertura && sessaoQuery.data
        ? {
            checklist: abertura.checklist,
            concluir: async (respostas) => {
              let inicio = historicoAnonimo.current?.inicio;
              if (!inicio) {
                const respostaHistorico =
                  await anonimoService.executaChecklistAnonimo({
                    ...respostas,
                    id: sessaoQuery.data.idChecklistAnonimo,
                    inicio: Date.now(),
                    fim: Date.now(),
                  });
                if (
                  !respostaHistorico ||
                  typeof respostaHistorico !== "object" ||
                  !Number.isInteger(
                    (respostaHistorico as { inicio?: unknown }).inicio,
                  ) ||
                  (respostaHistorico as { inicio: number }).inicio <= 0
                ) {
                  throw new Error(
                    "Não foi possível gravar as respostas do formulário. Tente novamente.",
                  );
                }
                inicio = (respostaHistorico as { inicio: number }).inicio;
                historicoAnonimo.current = { respostas, inicio };
              }
              return checkinService.concluir(
                aliasEmpresa as string,
                aliasChecklist as string,
                sessaoQuery.data.sessao,
                respostas,
                localizacao,
                inicio,
              );
            },
          }
        : undefined,
    [abertura, aliasChecklist, aliasEmpresa, localizacao, sessaoQuery.data],
  );

  if (sessaoQuery.isLoading)
    return (
      <Stack alignItems="center" padding={6}>
        <CircularProgress />
      </Stack>
    );
  if (sessaoQuery.isError || !sessaoQuery.data)
    return (
      <Stack alignItems="center" spacing={2} padding={6}>
        <Alert severity="error">{t("publicCheckin.invalidUrl")}</Alert>
      </Stack>
    );
  if (publicExecution)
    return <NewFormFilling publicExecution={publicExecution} />;

  const configuracao =
    sessaoQuery.data.configuracao ?? sessaoQuery.data.validacaoAgendamento;
  const confirmacaoSemAgendamento =
    configuracao?.confirmacaoSemAgendamento ||
    t("publicCheckin.fallbackConfirmation");
  const erro = validar.error ?? continuar.error ?? abrir.error;
  const buscando = validar.isPending || continuar.isPending || abrir.isPending;
  const agendamentoEncontrado = validacao?.agendamento ?? null;
  const semAgendamentoAutorizavel = Boolean(
    validacao &&
      !validacao.agendamento &&
      validacao.podeContinuarSemAgendamento,
  );
  const camposIdentificador = estrategiaQuery.data
    ? camposDaEstrategia(estrategiaQuery.data)
    : [];
  const identificadorPreenchido = Boolean(
    estrategiaQuery.data &&
      todosCamposIdAgendamentoPreenchidos(
        estrategiaQuery.data,
        valoresIdentificador,
      ),
  );

  return (
    <Stack
      maxWidth={560}
      margin="auto"
      padding={3}
      spacing={2}
      minHeight="100svh"
      justifyContent="center"
    >
      <Typography variant="h5" textAlign="center">
        {t("publicCheckin.title")}
      </Typography>
      <Typography color="text.secondary">
        {t("publicCheckin.description")}
      </Typography>
      {erro && <Alert severity="error">{t(mensagemErro(erro))}</Alert>}
      {estrategiaQuery.isError && (
        <Alert severity="error">
          {t("publicCheckin.errors.loadSchedulingStrategy")}
        </Alert>
      )}
      {avisoLocalizacao && abrir.isPending && (
        <Alert severity="info">{t("publicCheckin.locationRequired")}</Alert>
      )}
      {estrategiaQuery.isLoading && (
        <Stack alignItems="center" padding={2}>
          <CircularProgress size={28} />
        </Stack>
      )}
      {estrategiaQuery.data && (
        <>
          {camposIdentificador.map((campo, indice) => (
            <TextField
              key={campo}
              label={labelDoCampoIdAgendamento(campo)}
              value={valoresIdentificador[campo] ?? ""}
              onChange={(event) => {
                setValoresIdentificador((valores) => ({
                  ...valores,
                  [campo]: event.target.value,
                }));
                setValidacao(null);
              }}
              autoFocus={indice === 0}
              fullWidth
              disabled={buscando}
            />
          ))}
          <Button
            variant="contained"
            disabled={!identificadorPreenchido || buscando}
            onClick={() => validar.mutate()}
          >
            {validar.isPending
              ? t("publicCheckin.locating")
              : t("publicCheckin.continue")}
          </Button>
        </>
      )}
      {validacao &&
        !validacao.agendamento &&
        !validacao.podeContinuarSemAgendamento && (
          <Alert severity="info">
            {t("publicCheckin.noScheduleAttempts", {
              used: validacao.tentativas,
              max: validacao.tentativasMaximas,
            })}
          </Alert>
        )}
      <Dialog open={Boolean(agendamentoEncontrado)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("publicCheckin.confirmAppointment")}</DialogTitle>
        <DialogContent>
          <Typography>{t("publicCheckin.appointmentFound")}</Typography>
          {agendamentoEncontrado && (
            <ResumoAgendamento
              agendamento={agendamentoEncontrado}
              dataFormatada={t("publicCheckin.scheduledFor", {
                date: new Date(
                  agendamentoEncontrado.dataPrevista as number,
                ).toLocaleString(i18n.language),
              })}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidacao(null)}>
            {t("publicCheckin.back")}
          </Button>
          <Button
            variant="contained"
            disabled={abrir.isPending}
            onClick={() => abrir.mutate()}
          >
            {t("publicCheckin.start")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={semAgendamentoAutorizavel} maxWidth="xs" fullWidth>
        <DialogTitle>{t("publicCheckin.noSchedule")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>{confirmacaoSemAgendamento}</Typography>
            <Divider />
            <Alert severity="warning">{t("publicCheckin.reviewNotice")}</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidacao(null)}>
            {t("publicCheckin.tryAgain")}
          </Button>
          <Button
            variant="contained"
            disabled={buscando}
            onClick={() => continuar.mutate()}
          >
            {t("publicCheckin.continueWithout")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
